import { z } from "zod";
export const linkPreviewQuerySchema = z.object({
    url: z.string().url().max(2048),
    /** Skip the server's short-lived cache: the viewer asked for a fresh read. */
    refresh: z.literal("1").optional(),
}).strict();
export const linkPreviewResponseSchema = z.object({
    provider: z.enum(["google", "microsoft", "slack", "linear", "notion"]),
    status: z.enum(["ready", "not_connected", "reconnect", "access_denied", "not_found", "rate_limited", "unavailable", "unsupported"]),
    /** Why access failed when the viewer can act on it: the connection lacks the permission this read needs. */
    reason: z.enum(["missing_scope"]).optional(),
    title: z.string().max(240).nullable(),
    description: z.string().max(4000).nullable(),
    kind: z.string().max(160).nullable(),
    details: z.array(z.object({ label: z.string().max(80), value: z.string().max(400) }).strict()).max(8).optional(),
    warnings: z.array(z.string().max(240)).max(4).optional(),
    fetchedAt: z.number(),
}).strict();
const SLACK_TS = /^\d{10}\.\d{6}$/;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TEAMS_CONVERSATION = /^19:[\w.@-]+$/;
const TEAMS_MESSAGE = /^\d{1,20}$/;
const LINEAR_ISSUE = /^\/([\w-]+)\/issue\/([A-Z][A-Z0-9]{0,9}-\d{1,7})(?:\/[^/]*)?\/?$/;
const NOTION_ID = /([0-9a-f]{32})$/i;
/** A Notion page id in its canonical dashed form, from a URL's path or its `p` peek parameter. */
function notionPageId(url) {
    const raw = (url.searchParams.get("p") ?? url.pathname.split("/").filter(Boolean).at(-1) ?? "").replace(/-/g, "");
    const hex = NOTION_ID.exec(raw)?.[1]?.toLowerCase();
    return hex ? `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` : null;
}
/** Only provider-owned hosts and known resource paths enter authenticated readers. */
export function providerLink(href) {
    let url;
    try {
        url = new URL(href);
    }
    catch {
        return null;
    }
    if (url.protocol !== "https:" || url.username || url.password || url.port)
        return null;
    const host = url.hostname;
    const path = url.pathname;
    if (["m365.cloud.microsoft", "copilot.microsoft.com"].includes(host)) {
        const id = path.match(/\/(?:conversation|chats)\/([\w-]+)\/?$/)?.[1];
        if (id)
            return { provider: "microsoft", kind: "copilot-conversation", id, url: url.href };
    }
    if (["docs.google.com", "drive.google.com"].includes(host)) {
        // Published copies (/d/e/…) carry a publishing id, not a Drive file id; they are public pages.
        if (/\/d\/e\//.test(path))
            return null;
        const id = path.match(/\/(?:d|folders)\/([\w-]+)/)?.[1] ?? url.searchParams.get("id");
        if (id && /^[\w-]+$/.test(id))
            return { provider: "google", kind: "file", id, url: url.href };
    }
    if (host.endsWith(".slack.com")) {
        const match = path.match(/^\/archives\/([A-Z0-9]+)\/p(\d{10})(\d{6})\/?$/);
        const parent = url.searchParams.get("thread_ts") ?? undefined;
        if (match && (parent === undefined || SLACK_TS.test(parent)))
            return { provider: "slack", kind: "slack-message", container: match[1], id: `${match[2]}.${match[3]}`, parent, url: url.href };
    }
    if (["teams.microsoft.com", "teams.cloud.microsoft"].includes(host)) {
        const match = path.match(/^\/l\/message\/([^/]+)\/([^/]+)$/);
        if (match) {
            // Every part becomes a Graph path segment, so each must have its expected shape.
            let container;
            let id;
            try {
                container = decodeURIComponent(match[1]);
                id = decodeURIComponent(match[2]);
            }
            catch {
                return null;
            }
            const team = url.searchParams.get("groupId") ?? undefined;
            const parent = url.searchParams.get("parentMessageId") ?? undefined;
            if (!TEAMS_CONVERSATION.test(container) || !TEAMS_MESSAGE.test(id))
                return null;
            if ((team !== undefined && !GUID.test(team)) || (parent !== undefined && !TEAMS_MESSAGE.test(parent)))
                return null;
            return { provider: "microsoft", kind: "teams-message", container, id, team, parent, url: url.href };
        }
    }
    if (host === "linear.app") {
        const match = LINEAR_ISSUE.exec(path);
        if (match)
            return { provider: "linear", kind: "linear-issue", container: match[1], id: match[2], url: url.href };
    }
    // Workspace pages only: a published *.notion.site page is public and read as a web page.
    if (host === "www.notion.so" || host === "notion.so") {
        const id = notionPageId(url);
        if (id)
            return { provider: "notion", kind: "notion-page", id, url: url.href };
    }
    if (host.endsWith(".sharepoint.com") || host === "onedrive.live.com" || host === "1drv.ms") {
        return { provider: "microsoft", kind: "file", id: url.href, url: url.href };
    }
    return null;
}
